import React, { useState, useEffect, useRef } from 'react';

const OrderBook = ({ orders, product }) => {
  const [bids, setBids] = useState([]);
  const [asks, setAsks] = useState([]);
  const asksRef = useRef(null);

  useEffect(() => {
    // Initialize orderbook with full snapshot
    if (orders && orders.bids && orders.asks) {
      const sortedBids = orders.bids
        .filter(order => !order.action || order.action !== 'remove')
        .sort((a, b) => b.price - a.price);  // Sort bids high to low
      const sortedAsks = orders.asks
        .filter(order => !order.action || order.action !== 'remove')
        .sort((a, b) => a.price - b.price);  // Sort asks low to high
      setBids(sortedBids);
      setAsks(sortedAsks);
      // console.log('Initial Orderbook Snapshot:');
      // console.log('Bids:', sortedBids);
      // console.log('Asks:', sortedAsks);
    }
  }, [product]); // Reset when product changes

  useEffect(() => {
    if (!orders) return;

    // Handle full orderbook update
    if (orders.bids && orders.asks && !orders.delta) {
      const sortedBids = orders.bids
        .filter(order => !order.action || order.action !== 'remove')
        .sort((a, b) => b.price - a.price);
      const sortedAsks = orders.asks
        .filter(order => !order.action || order.action !== 'remove')
        .sort((a, b) => a.price - b.price);
      setBids(sortedBids);
      setAsks(sortedAsks);
      // console.log('Full Orderbook Update:');
      // console.log('Bids:', sortedBids);
      // console.log('Asks:', sortedAsks);
      return;
    }

    // Handle delta updates
    if (orders.delta) {
      const applyDelta = (currentOrders, deltaOrders) => {
        // Create a map of current orders for easy lookup and modification
        const orderMap = new Map(
          currentOrders.map(order => [order.price, order])
        );

        // Apply each delta operation
        for (const delta of deltaOrders) {
          if (delta.action === 'remove') {
            orderMap.delete(delta.price);
          } else if (delta.action === 'add' || !delta.action) {
            // Only add if quantity is valid
            if (delta.quantity && delta.quantity > 0) {
              orderMap.set(delta.price, {
                price: delta.price,
                quantity: delta.quantity
              });
            }
          }
        }

        // Convert back to array, filter out any undefined entries or zero quantities, and sort
        return Array.from(orderMap.values())
          .filter(order => order !== undefined && order.quantity && order.quantity > 0)
          .sort((a, b) => b.price - a.price);
      };

      if (orders.delta.bids) {
        setBids(prevBids => {
          const updatedBids = applyDelta(prevBids, orders.delta.bids);
          // console.log('Bids Delta Update:', {
          //   delta: orders.delta.bids,
          //   updatedBids
          // });
          return updatedBids;
        });
      }
      
      if (orders.delta.asks) {
        setAsks(prevAsks => {
          const updatedAsks = applyDelta(prevAsks, orders.delta.asks);
          // console.log('Asks Delta Update:', {
          //   delta: orders.delta.asks,
          //   updatedAsks
          // });
          return updatedAsks.sort((a, b) => a.price - b.price);
        });
      }
    }
  }, [orders]);

  // Effect to maintain scroll position at bottom
  useEffect(() => {
    if (asksRef.current) {
      asksRef.current.scrollTop = asksRef.current.scrollHeight;
    }
  }, [asks]);

  const renderOrders = (orders, isBids) => {
    // For asks, we want to display them in reverse order (highest at bottom)
    const displayOrders = isBids ? orders : [...orders];
    
    return displayOrders.map((order, index) => (
      <div key={`${order.price}-${index}`} className={`order-entry ${isBids ? 'bid' : 'ask'}`}>
        <span className="price">{order.price}</span>
        <span className="quantity">{order.quantity}</span>
        <span className="total">{(order.price * order.quantity).toFixed(2)}</span>
      </div>
    ));
  };

  return (
    <div className="order-book">
      <h2>{product} Order Book</h2>
      <div className="order-book-table">
        <div className="column-headers">
          <span>Price</span>
          <span>Quantity</span>
          <span>Total</span>
        </div>
        <div className="asks" ref={asksRef}>
          {renderOrders(asks, false)}
        </div>
        <div className="mid-price">
          {asks.length > 0 && bids.length > 0 
            ? `Mid: ${((asks[0].price + bids[0].price) / 2).toFixed(2)}`
            : 'Mid: -'
          }
        </div>
        <div className="bids">
          {renderOrders(bids, true)}
        </div>
      </div>
    </div>
  );
};

export default OrderBook;